import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  waMessageService,
  WaConversationFilters,
  WaMessageFilters,
  SendMessageRequest,
  SendBulkMessageRequest,
} from '@services/wa-message.service'

// Conversations
export function useWaConversations(filters: WaConversationFilters = {}) {
  return useQuery({
    queryKey: ['waConversations', filters],
    queryFn: () => waMessageService.fetchConversations(filters),
    refetchInterval: 5000, // Poll every 5 seconds for new conversations/updates
  })
}

export function useWaConversation(id: number) {
  return useQuery({
    queryKey: ['waConversation', id],
    queryFn: () => waMessageService.getConversation(id),
    enabled: !!id,
  })
}

export function useArchiveWaConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waMessageService.archiveConversation(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waConversations'] })
      queryClient.invalidateQueries({ queryKey: ['waConversation', id] })
    },
  })
}

export function useUnarchiveWaConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waMessageService.unarchiveConversation(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waConversations'] })
      queryClient.invalidateQueries({ queryKey: ['waConversation', id] })
    },
  })
}

export function usePinWaConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waMessageService.pinConversation(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waConversations'] })
      queryClient.invalidateQueries({ queryKey: ['waConversation', id] })
    },
  })
}

export function useUnpinWaConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waMessageService.unpinConversation(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waConversations'] })
      queryClient.invalidateQueries({ queryKey: ['waConversation', id] })
    },
  })
}

export function useMarkWaConversationAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waMessageService.markConversationAsRead(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waConversations'] })
      queryClient.invalidateQueries({ queryKey: ['waConversation', id] })
      queryClient.invalidateQueries({ queryKey: ['waInboxStats'] })
    },
  })
}

// Messages
export function useWaMessages(filters: WaMessageFilters = {}) {
  return useQuery({
    queryKey: ['waMessages', filters],
    queryFn: () => waMessageService.fetchMessages(filters),
  })
}

export function useWaMessage(id: number) {
  return useQuery({
    queryKey: ['waMessage', id],
    queryFn: () => waMessageService.getMessage(id),
    enabled: !!id,
  })
}

export function useWaConversationMessages(
  contactId: number,
  accountId: number,
  params?: { page?: number; limit?: number }
) {
  return useQuery({
    queryKey: ['waConversationMessages', contactId, accountId, params],
    queryFn: () => waMessageService.getConversationMessages(contactId, accountId, params),
    enabled: !!contactId && !!accountId,
    refetchInterval: 3000, // Poll every 3 seconds for new messages in active conversation
  })
}

export function useSendWaMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SendMessageRequest) => waMessageService.sendMessage(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['waMessages'] })
      queryClient.invalidateQueries({ queryKey: ['waConversations'] })
      queryClient.invalidateQueries({
        queryKey: ['waConversationMessages', variables.contactId, variables.accountId],
      })
      queryClient.invalidateQueries({ queryKey: ['waInboxStats'] })
    },
  })
}

export function useSendBulkWaMessages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SendBulkMessageRequest) => waMessageService.sendBulkMessages(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waMessages'] })
      queryClient.invalidateQueries({ queryKey: ['waConversations'] })
      queryClient.invalidateQueries({ queryKey: ['waInboxStats'] })
    },
  })
}

// Inbox Stats
export function useWaInboxStats(accountId?: number) {
  return useQuery({
    queryKey: ['waInboxStats', accountId],
    queryFn: () => waMessageService.getInboxStats(accountId),
    refetchInterval: 30000, // Refresh every 30 seconds
  })
}

// Contact Profile Picture
export function useWaContactProfilePicture(contactId: number, accountId?: number) {
  return useQuery({
    queryKey: ['waContactProfilePicture', contactId, accountId],
    queryFn: () => waMessageService.getContactProfilePicture(contactId, accountId),
    enabled: !!contactId,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  })
}
